import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SendApprovalRejectModal from "../../Components/Common/SendApprovalRejectModal";
import {
  BounceKindFilter,
  CampaignSend,
  CampaignSendRecipient,
  CampaignSendRecipientStatus,
  CampaignSendStatus,
  approveCampaignSend,
  cancelCampaignSend,
  cancelPartialChain,
  downloadExcludedContactsCsv,
  downloadRecipientsCsv,
  formatRatePercent,
  getSend,
  getSendRecipients,
  isBounceRateHigh,
  clampBounceWarnThreshold,
  BOUNCE_RATE_WARN_THRESHOLD,
  isSendCancellable,
  isSendInProgress,
  pausePartialChain,
  queuePartialRemainder,
  RecipientEngagementFilter,
  rejectCampaignSend,
  resumePartialChain,
  tagSendRecipients,
  updateSendLinkGoal,
} from "../../helpers/sends_helper";
import { fetchAllTags, Tag } from "../../helpers/audience_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../helpers/tenantTimezone";
import { helpArticleUrl, helpUrlForRoute } from "../../constants/helpRouteMap";
import { canApproveSend, isOwner } from "../../utils/tenantRoles";
import { getByoCircuitSettings } from "../../helpers/byo_circuit_helper";
import { getWorkspaceSettings } from "../../helpers/workspace_settings_helper";
import {
  formatCircuitAutoResumeCountdown,
  hasCircuitAutoResumeEta,
} from "../../helpers/circuitAutoResumeEta";
import { useCircuitAutoResumeNowMs } from "../../helpers/useCircuitAutoResumeNowMs";

const POLL_MS = 4000;
const RECIPIENTS_PER_PAGE = 20;
const RECIPIENT_STATUSES: CampaignSendRecipientStatus[] = [
  "pending",
  "sent",
  "skipped",
  "bounced",
  "complained",
  "failed",
];
const ENGAGEMENT_FILTERS: RecipientEngagementFilter[] = ["opened", "clicked"];
const BOUNCE_KIND_FILTERS: Array<"hard" | "soft"> = ["hard", "soft"];

const statusColor = (status: CampaignSendStatus): string => {
  switch (status) {
    case "completed":
      return "success";
    case "sending":
    case "preparing":
    case "queued":
      return "info";
    case "failed":
    case "rejected":
      return "danger";
    case "pending_approval":
      return "warning";
    case "cancelled":
    case "paused":
      return "warning";
    default:
      return "secondary";
  }
};

const recipientStatusColor = (status: string): string => {
  switch (status) {
    case "sent":
      return "success";
    case "bounced":
    case "failed":
    case "complained":
      return "danger";
    case "pending":
      return "info";
    case "skipped":
      return "secondary";
    default:
      return "secondary";
  }
};

type KpiProps = {
  label: string;
  value: string | number;
  icon: string;
  hint?: string;
  onClick?: () => void;
};

const KpiCard = ({ label, value, icon, hint, onClick }: KpiProps) => (
  <Card
    className={`card-animate h-100${onClick ? " border-primary border-opacity-25" : ""}`}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    title={hint}
    style={onClick ? { cursor: "pointer" } : undefined}
  >
    <CardBody>
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <p className="text-uppercase fw-medium text-muted mb-0">
            {label}
            {hint ? (
              <i
                className="ri-information-line ms-1 align-middle"
                aria-hidden="true"
              />
            ) : null}
          </p>
          <h4 className="fs-22 fw-semibold mb-0 mt-2">{value}</h4>
          {hint ? (
            <p className="text-muted small mb-0 mt-2" style={{ lineHeight: 1.35 }}>
              {hint}
            </p>
          ) : null}
        </div>
        <div className="avatar-sm flex-shrink-0">
          <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
            <i className={icon} />
          </span>
        </div>
      </div>
    </CardBody>
  </Card>
);

const parseRecipientStatus = (raw: string | null): string => {
  if (!raw) {
    return "";
  }
  return RECIPIENT_STATUSES.includes(raw as CampaignSendRecipientStatus)
    ? raw
    : "";
};

const parseEngagement = (raw: string | null): RecipientEngagementFilter => {
  if (raw === "opened" || raw === "clicked") {
    return raw;
  }
  return "";
};

const parseBounceKind = (raw: string | null): BounceKindFilter => {
  if (raw === "hard" || raw === "soft") {
    return raw;
  }
  return "";
};

/**
 * Detalhe / métricas + drill-down (LAC-035 polish: URL status, link contato, CSV).
 */
const SendDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sendId = Number(id);
  const locale = i18n.language || "pt-BR";
  const tenantTimezone = useMemo(() => readDisplayTimezone(), []);
  const canaryHelpHref = helpUrlForRoute("/sends", locale);

  const formatTenantDate = (iso: string | null | undefined) =>
    formatInTenantTimezone(iso, tenantTimezone, locale);

  const [send, setSend] = useState<CampaignSend | null>(null);
  // Limiar do aviso de bounce (Preferências da conta) — LAC-191.
  const [bounceWarnThreshold, setBounceWarnThreshold] = useState(
    BOUNCE_RATE_WARN_THRESHOLD
  );
  // Link para Controles de envio só se a conta for BYO (owner).
  const [byoControlsAvailable, setByoControlsAvailable] = useState(false);
  const [loading, setLoading] = useState(true);

  // Countdown leigo: “faltam ~12 min” sem reload (LAC-138; helper compartilhado com lista LAC-194).
  const etaTickActive = hasCircuitAutoResumeEta({
    status: send?.status,
    pause_reason: send?.pause_reason,
    circuit_auto_resume_eta_at: send?.circuit_auto_resume_eta_at,
    needs_sa_resume: send?.needs_sa_resume,
  });
  const etaNowMs = useCircuitAutoResumeNowMs(etaTickActive);
  const autoResumeCountdownLabel = useMemo(
    () =>
      formatCircuitAutoResumeCountdown(
        send?.circuit_auto_resume_eta_at,
        send?.needs_sa_resume,
        etaNowMs,
        t,
        "detail"
      ),
    [
      send?.circuit_auto_resume_eta_at,
      send?.needs_sa_resume,
      etaNowMs,
      t,
    ]
  );
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const [recipients, setRecipients] = useState<CampaignSendRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  const [recipientStatus, setRecipientStatus] = useState(() =>
    parseRecipientStatus(searchParams.get("status"))
  );
  const [recipientEngagement, setRecipientEngagement] =
    useState<RecipientEngagementFilter>(() =>
      parseEngagement(searchParams.get("engagement"))
    );
  const [recipientBounceKind, setRecipientBounceKind] =
    useState<BounceKindFilter>(() =>
      parseBounceKind(searchParams.get("bounce_kind"))
    );
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientLastPage, setRecipientLastPage] = useState(1);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [exportBusy, setExportBusy] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagOptions, setTagOptions] = useState<Tag[]>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagId, setTagId] = useState<number | "">("");
  const [tagBusy, setTagBusy] = useState(false);
  const [linkGoalBusy, setLinkGoalBusy] = useState<number | null>(null);
  const [remainderBusy, setRemainderBusy] = useState(false);
  const [cancelChainOpen, setCancelChainOpen] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!Number.isFinite(sendId) || sendId < 1) {
        setError(t("sending.error.loadOne"));
        setLoading(false);
        return;
      }
      if (!silent) {
        setLoading(true);
      }
      try {
        const res = await getSend(sendId);
        if (!res?.success) {
          setError(t("sending.error.loadOne"));
          setSend(null);
          return;
        }
        const next = res.data;
        // Toast 1× quando um dia seguinte da cadeia começa a sair.
        const nodes = next.audience?.partial_chain?.nodes ?? [];
        for (const node of nodes) {
          if (
            (node.day_index ?? 0) > 1 &&
            node.status === "sending" &&
            typeof sessionStorage !== "undefined"
          ) {
            const key = `partial-remainder-started-${node.id}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, "1");
              toast.info(
                t("sending.detail.partialRemainderStartedToast", {
                  id: node.id,
                  day: node.day_index,
                })
              );
            }
          }
        }
        setSend(next);
        setError(null);
        document.title = t("sending.detail.documentTitle", {
          subject: next.subject,
        });
      } catch (e: any) {
        if (!silent) {
          setError(e?.message || t("sending.error.loadOne"));
          setSend(null);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [sendId, t]
  );

  const loadRecipients = useCallback(async () => {
    if (!Number.isFinite(sendId) || sendId < 1) {
      return;
    }
    setRecipientsLoading(true);
    setRecipientsError(null);
    try {
      const res = await getSendRecipients(sendId, {
        status: (recipientStatus || undefined) as
          | CampaignSendRecipientStatus
          | undefined,
        engagement: recipientEngagement || undefined,
        bounce_kind: recipientBounceKind || undefined,
        page: recipientPage,
        per_page: RECIPIENTS_PER_PAGE,
      });
      if (!res?.success) {
        setRecipientsError(t("sending.recipients.error"));
        setRecipients([]);
        return;
      }
      setRecipients(res.data || []);
      setRecipientTotal(res.meta?.total ?? 0);
      setRecipientLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setRecipientsError(e?.message || t("sending.recipients.error"));
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  }, [
    sendId,
    recipientStatus,
    recipientEngagement,
    recipientBounceKind,
    recipientPage,
    t,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Limiar do aviso de bounce (Preferências) — falha silenciosa → default 5%.
  useEffect(() => {
    let cancelled = false;
    getWorkspaceSettings()
      .then((res) => {
        if (cancelled || !res?.data) return;
        setBounceWarnThreshold(
          clampBounceWarnThreshold(res.data.bounce_rate_warn_threshold_percent)
        );
      })
      .catch(() => {
        /* mantém default */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Controles BYO: só consulta se owner e envio pausado por circuit/platform.
  useEffect(() => {
    if (!isOwner()) {
      setByoControlsAvailable(false);
      return;
    }
    if (
      send?.status !== "paused" ||
      (send.pause_reason !== "circuit" && send.pause_reason !== "platform")
    ) {
      return;
    }
    let cancelled = false;
    getByoCircuitSettings()
      .then((res) => {
        if (!cancelled) setByoControlsAvailable(!!res?.data?.available);
      })
      .catch(() => {
        if (!cancelled) setByoControlsAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [send?.status, send?.pause_reason]);

  useEffect(() => {
    if (send) {
      loadRecipients();
    }
  }, [send, loadRecipients]);

  useEffect(() => {
    if (!send || !isSendInProgress(send.status)) {
      return;
    }
    const timer = window.setInterval(() => {
      load(true);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [send?.status, load, send]);

  // Deep-link / sync: ?status=bounced&engagement=opened&bounce_kind=hard
  useEffect(() => {
    const fromUrlStatus = parseRecipientStatus(searchParams.get("status"));
    const fromUrlEngagement = parseEngagement(searchParams.get("engagement"));
    const fromUrlBounceKind = parseBounceKind(searchParams.get("bounce_kind"));
    if (fromUrlStatus !== recipientStatus) {
      setRecipientStatus(fromUrlStatus);
      setRecipientPage(1);
    }
    if (fromUrlEngagement !== recipientEngagement) {
      setRecipientEngagement(fromUrlEngagement);
      setRecipientPage(1);
    }
    if (fromUrlBounceKind !== recipientBounceKind) {
      setRecipientBounceKind(fromUrlBounceKind);
      setRecipientPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const syncRecipientQuery = (
    status: string,
    engagement: string,
    bounceKind: string = recipientBounceKind
  ) => {
    const next = new URLSearchParams(searchParams);
    if (status) {
      next.set("status", status);
    } else {
      next.delete("status");
    }
    if (engagement) {
      next.set("engagement", engagement);
    } else {
      next.delete("engagement");
    }
    if (bounceKind) {
      next.set("bounce_kind", bounceKind);
    } else {
      next.delete("bounce_kind");
    }
    setSearchParams(next, { replace: true });
  };

  const confirmCancel = async () => {
    if (!send) {
      return;
    }
    setCancelBusy(true);
    try {
      const res = await cancelCampaignSend(send.id);
      toast.success(res?.message || t("sending.toast.cancelled"), {
        autoClose: 2500,
      });
      setCancelOpen(false);
      setSend((current) => (current ? { ...current, ...res.data } : res.data));
    } catch (e: any) {
      toast.error(e?.message || t("sending.error.cancel"), { autoClose: 4000 });
    } finally {
      setCancelBusy(false);
    }
  };

  const approve = async () => {
    if (!send || !window.confirm(t("sending.approval.approveConfirm"))) return;
    setApprovalBusy(true);
    try {
      const res = await approveCampaignSend(send.id);
      setSend((current) => (current ? { ...current, ...res.data } : res.data));
      toast.success(res.message || t("sending.approval.approved"));
    } catch (e: any) {
      toast.error(e?.message || t("sending.approval.error"));
    } finally {
      setApprovalBusy(false);
    }
  };

  const reject = async () => {
    if (!send) return;
    setApprovalBusy(true);
    try {
      const res = await rejectCampaignSend(send.id, rejectionReason);
      setSend((current) => (current ? { ...current, ...res.data } : res.data));
      setRejectOpen(false);
      setRejectionReason("");
      toast.success(res.message || t("sending.approval.rejected"));
    } catch (e: any) {
      toast.error(e?.message || t("sending.approval.error"));
    } finally {
      setApprovalBusy(false);
    }
  };

  const onRecipientStatusChange = (value: string) => {
    setRecipientStatus(value);
    // Bounce kind só faz sentido com status=bounced.
    const nextBounce =
      value === "bounced" || value === "" ? recipientBounceKind : "";
    if (value !== "bounced" && value !== "") {
      setRecipientBounceKind("");
    }
    setRecipientPage(1);
    syncRecipientQuery(value, recipientEngagement, nextBounce);
  };

  const onRecipientEngagementChange = (value: string) => {
    const next = parseEngagement(value);
    setRecipientEngagement(next);
    setRecipientPage(1);
    syncRecipientQuery(recipientStatus, next, recipientBounceKind);
  };

  const onRecipientBounceKindChange = (value: string) => {
    const next = parseBounceKind(value);
    setRecipientBounceKind(next);
    // Filtrar por tipo implica olhar os não entregues.
    const nextStatus = next ? "bounced" : recipientStatus;
    if (next) {
      setRecipientStatus("bounced");
    }
    setRecipientPage(1);
    syncRecipientQuery(nextStatus, recipientEngagement, next);
  };

  const scrollToRecipients = () => {
    window.setTimeout(() => {
      document
        .getElementById("send-recipients")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const filterByEngagement = (engagement: RecipientEngagementFilter) => {
    setRecipientEngagement(engagement);
    setRecipientStatus("");
    setRecipientBounceKind("");
    setRecipientPage(1);
    syncRecipientQuery("", engagement, "");
    // Rola até a lista — Carla vê o efeito do clique no KPI.
    scrollToRecipients();
  };

  const filterByStatus = (status: string) => {
    setRecipientStatus(status);
    setRecipientEngagement("");
    setRecipientBounceKind("");
    setRecipientPage(1);
    syncRecipientQuery(status, "", "");
    scrollToRecipients();
  };

  const filterByBounceKind = (kind: BounceKindFilter) => {
    setRecipientBounceKind(kind);
    setRecipientStatus(kind ? "bounced" : "");
    setRecipientEngagement("");
    setRecipientPage(1);
    syncRecipientQuery(kind ? "bounced" : "", "", kind);
    scrollToRecipients();
  };

  /** Destinatários (total) — só rola até a lista, sem inventar filtro. */
  const focusRecipientsList = () => {
    setRecipientStatus("");
    setRecipientEngagement("");
    setRecipientBounceKind("");
    setRecipientPage(1);
    syncRecipientQuery("", "", "");
    scrollToRecipients();
  };

  const copySendSnapshot = async () => {
    if (!send) {
      return;
    }
    const pending = send.pending_count ?? 0;
    const lines = [
      send.campaign?.name ||
        t("sending.list.campaignFallback", { id: send.campaign_id }),
      send.subject,
      t(`sending.status.${send.status}`),
      t("sending.detail.copySnapshot.progress", {
        sent: send.sent,
        queued: send.queued,
      }),
    ];
    if (pending > 0) {
      lines.push(t("sending.detail.copySnapshot.pending", { pending }));
    }
    lines.push(
      t("sending.detail.copySnapshot.rates", {
        open: formatRatePercent(send.open_rate, locale),
        click: formatRatePercent(send.click_rate, locale),
        bounce: formatRatePercent(send.bounce_rate, locale),
      })
    );
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success(t("sending.detail.copySnapshot.done"), { autoClose: 2000 });
    } catch {
      toast.error(t("sending.detail.copySnapshot.error"), { autoClose: 3500 });
    }
  };

  const openTagModal = async () => {
    if (!recipientStatus && !recipientEngagement && !recipientBounceKind) {
      toast.info(t("sending.recipients.tagNeedFilter"), { autoClose: 3500 });
      return;
    }
    setTagModalOpen(true);
    setTagId("");
    setTagLoading(true);
    try {
      const list = await fetchAllTags();
      setTagOptions(list);
    } catch {
      setTagOptions([]);
      toast.error(t("sending.recipients.tagError"), { autoClose: 3500 });
    } finally {
      setTagLoading(false);
    }
  };

  const confirmTag = async () => {
    if (!tagId || !Number.isFinite(sendId)) {
      return;
    }
    setTagBusy(true);
    try {
      const res = await tagSendRecipients(sendId, {
        tag_id: Number(tagId),
        status: (recipientStatus || undefined) as
          | CampaignSendRecipientStatus
          | undefined,
        engagement: recipientEngagement || undefined,
        bounce_kind: recipientBounceKind || undefined,
      });
      toast.success(
        res?.message ||
          t("sending.recipients.tagDone", { count: res?.data?.tagged ?? 0 }),
        { autoClose: 3000 }
      );
      setTagModalOpen(false);
    } catch (e: any) {
      toast.error(e?.message || t("sending.recipients.tagError"), {
        autoClose: 4000,
      });
    } finally {
      setTagBusy(false);
    }
  };

  const toggleLinkGoal = async (linkId: number, isGoal: boolean) => {
    if (!Number.isFinite(sendId)) {
      return;
    }
    setLinkGoalBusy(linkId);
    try {
      const res = await updateSendLinkGoal(sendId, linkId, !isGoal);
      if (res?.success && res.data) {
        setSend(res.data);
      }
    } catch (e: any) {
      toast.error(e?.message || t("sending.error.generic"), {
        autoClose: 4000,
      });
    } finally {
      setLinkGoalBusy(null);
    }
  };

  const exportFilteredCsv = async () => {
    if (!Number.isFinite(sendId) || sendId < 1) {
      return;
    }
    setExportBusy(true);
    try {
      const all: CampaignSendRecipient[] = [];
      let page = 1;
      let last = 1;
      do {
        const res = await getSendRecipients(sendId, {
          status: (recipientStatus || undefined) as
            | CampaignSendRecipientStatus
            | undefined,
          engagement: recipientEngagement || undefined,
          bounce_kind: recipientBounceKind || undefined,
          page,
          per_page: 100,
        });
        if (!res?.success) {
          throw new Error(t("sending.recipients.error"));
        }
        all.push(...(res.data || []));
        last = res.meta?.last_page ?? 1;
        page += 1;
        if (all.length > 5000) {
          break;
        }
      } while (page <= last);

      if (all.length === 0) {
        toast.info(t("sending.recipients.exportEmpty"), { autoClose: 2500 });
        return;
      }

      const suffix =
        [recipientStatus, recipientEngagement, recipientBounceKind]
          .filter(Boolean)
          .join("-") || "all";
      downloadRecipientsCsv(all, `send-${sendId}-recipients-${suffix}.csv`, {
        hard: t("sending.recipients.bounceKind.hard"),
        soft: t("sending.recipients.bounceKind.soft"),
      });
      toast.success(t("sending.recipients.exportDone", { count: all.length }), {
        autoClose: 2500,
      });
    } catch (e: any) {
      toast.error(e?.message || t("sending.recipients.exportError"), {
        autoClose: 4000,
      });
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("sending.detail.title")}
            pageTitle={t("sending.list.title")}
          />
          <div className="mb-3 d-flex flex-wrap gap-2">
            <Button color="light" size="sm" tag={Link} to="/sends">
              {t("sending.detail.back")}
            </Button>
            {send?.campaign_id ? (
              <>
                <Button
                  color="soft-success"
                  size="sm"
                  tag={Link}
                  to={`/campaigns/${send.campaign_id}/send`}
                  title={t("sending.detail.resendTitle")}
                >
                  <i className="ri-send-plane-fill me-1 align-middle" aria-hidden />
                  {t("sending.detail.resend")}
                </Button>
                <Button
                  color="soft-primary"
                  size="sm"
                  tag={Link}
                  to={`/campaigns/${send.campaign_id}/edit`}
                  title={t("sending.detail.editCampaignTitle")}
                >
                  <i className="ri-pencil-fill me-1 align-middle" aria-hidden />
                  {t("sending.detail.editCampaign")}
                </Button>
              </>
            ) : null}
            {send && isSendCancellable(send.status) && (
              <Button
                color="soft-danger"
                size="sm"
                onClick={() => setCancelOpen(true)}
              >
                {t("sending.detail.cancel")}
              </Button>
            )}
            {send?.status === "pending_approval" && canApproveSend() ? (
              <>
                <Button color="success" size="sm" disabled={approvalBusy} onClick={() => void approve()}>
                  {t("sending.approval.approve")}
                </Button>
                <Button color="danger" size="sm" outline disabled={approvalBusy} onClick={() => setRejectOpen(true)}>
                  {t("sending.approval.reject")}
                </Button>
              </>
            ) : null}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : error || !send ? (
            <Alert color="danger">{error || t("sending.error.loadOne")}</Alert>
          ) : (
            <>
              {send.status === "pending_approval" && (
                <Alert color="warning" className="mb-3">
                  <strong>{t("sending.approval.pendingTitle")}</strong>
                  <div>{t("sending.approval.pendingHelp")}</div>
                </Alert>
              )}
              {send.status === "rejected" && (
                <Alert color="danger" className="mb-3">
                  <strong>{t("sending.approval.rejectedTitle")}</strong>
                  <div>{send.rejection_reason || t("sending.approval.rejectedHelp")}</div>
                </Alert>
              )}
              {send.status === "sending" && send.waiting_for_capacity && (
                  <Alert color="warning" className="mb-3">
                    <p className="mb-1 fw-medium">
                      {t("sending.detail.waitingCapacity.title")}
                    </p>
                    <p className="mb-0">
                      {t("sending.detail.waitingCapacity.body", {
                        pending: send.pending_count ?? 0,
                        sent: send.sent,
                        queued: send.queued,
                      })}
                    </p>
                  </Alert>
                )}
              {isSendInProgress(send.status) &&
                (send.canary_pilot_size ?? 0) > 0 &&
                !send.waiting_for_capacity && (
                  <Alert color="info" className="mb-3 py-2">
                    {t("sending.detail.canaryVerifying")}
                  </Alert>
                )}
              {isSendInProgress(send.status) &&
                !(send.canary_pilot_size ?? 0) &&
                !send.waiting_for_capacity &&
                (send.pending_count ?? 0) > 0 && (
                  <Alert color="info" className="mb-3 py-2">
                    {t("sending.detail.fairQueue", {
                      pending: send.pending_count ?? 0,
                      sent: send.sent,
                      queued: send.queued,
                    })}
                  </Alert>
                )}
              {isSendInProgress(send.status) &&
                !(send.canary_pilot_size ?? 0) &&
                !send.waiting_for_capacity &&
                !(send.pending_count ?? 0) && (
                  <Alert color="info" className="mb-3 py-2">
                    {t("sending.detail.polling")}
                  </Alert>
                )}
              {send.status === "paused" && (
                <Alert
                  color="warning"
                  className="mb-3 d-flex flex-column flex-md-row flex-wrap justify-content-between align-items-md-center gap-2"
                >
                  <div>
                    <p className="mb-1 fw-medium">
                      {t(
                        send.pause_reason === "deadline"
                          ? "sending.detail.deadlinePaused.title"
                          : send.pause_reason === "circuit" ||
                              send.pause_reason === "platform"
                            ? "sending.detail.circuitPaused.title"
                            : "sending.detail.canaryPaused.title"
                      )}
                    </p>
                    <p className="mb-0">
                      {t(
                        send.pause_reason === "deadline"
                          ? "sending.detail.deadlinePaused.body"
                          : send.pause_reason === "circuit" ||
                              send.pause_reason === "platform"
                            ? send.needs_sa_resume
                              ? "sending.detail.circuitPaused.bodyNeedsSa"
                              : "sending.detail.circuitPaused.bodyMayAuto"
                            : "sending.detail.canaryPaused.body"
                      )}
                    </p>
                    {send.pause_reason === "circuit" ||
                    send.pause_reason === "platform"
                      ? send.circuit_auto_resume_eta_at &&
                        !send.needs_sa_resume && (
                          <p className="mb-0 mt-2 small text-muted">
                            {autoResumeCountdownLabel
                              ? autoResumeCountdownLabel
                              : t("sending.detail.circuitPaused.eta", {
                                  when: formatTenantDate(
                                    send.circuit_auto_resume_eta_at
                                  ),
                                })}
                            {autoResumeCountdownLabel ? (
                              <>
                                {" "}
                                (
                                {t("sending.detail.circuitPaused.eta", {
                                  when: formatTenantDate(
                                    send.circuit_auto_resume_eta_at
                                  ),
                                })}
                                )
                              </>
                            ) : null}
                          </p>
                        )
                      : null}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {(send.pause_reason === "circuit" ||
                      send.pause_reason === "platform") && (
                      <>
                        <Button
                          color="warning"
                          size="sm"
                          outline
                          onClick={() => {
                            void navigator.clipboard.writeText(String(send.id)).then(
                              () =>
                                toast.success(
                                  t("sending.detail.copySendIdSuccess", {
                                    id: send.id,
                                  }),
                                  { autoClose: 2000 }
                                ),
                              () =>
                                toast.error(t("sending.detail.copySendIdError"), {
                                  autoClose: 3000,
                                })
                            );
                          }}
                        >
                          {t("sending.detail.copySendId", { id: send.id })}
                        </Button>
                        {byoControlsAvailable && (
                          <Button
                            color="warning"
                            size="sm"
                            outline
                            tag={Link}
                            to="/settings/sending-controls"
                          >
                            {t("sending.detail.circuitPaused.openControls")}
                          </Button>
                        )}
                      </>
                    )}
                    {(send.pause_reason === "canary" ||
                      !send.pause_reason) && (
                      <Button
                        color="warning"
                        size="sm"
                        outline
                        onClick={() => onRecipientStatusChange("bounced")}
                      >
                        {t("sending.detail.canaryPaused.reviewBounces")}
                      </Button>
                    )}
                    {send.pause_reason !== "deadline" && (
                      <Button
                        color="warning"
                        size="sm"
                        outline
                        tag="a"
                        href={
                          send.pause_reason === "circuit" ||
                          send.pause_reason === "platform"
                            ? helpArticleUrl("/sends/why-paused", locale)
                            : canaryHelpHref
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t(
                          send.pause_reason === "circuit" ||
                            send.pause_reason === "platform"
                            ? "sending.detail.circuitPaused.help"
                            : "sending.detail.canaryPaused.help"
                        )}
                      </Button>
                    )}
                  </div>
                </Alert>
              )}
              {send.audience?.partial_sample && (
                <Alert color="info" className="mb-3 py-2">
                  <p className="mb-2">
                    {t("sending.detail.partialSample", {
                      selected:
                        send.audience.selected_count ?? send.queued ?? 0,
                      total: send.audience.eligible_total ?? send.queued ?? 0,
                      sampleBy:
                        send.audience.sample_by === "random"
                          ? t("sending.detail.partialSampleByRandom")
                          : t("sending.detail.partialSampleByEngagement"),
                    })}
                  </p>
                  {(send.audience.partial_day_index ?? 0) > 0 && (
                    <p className="mb-2 small fw-medium">
                      {t("sending.detail.partialProgress", {
                        day: send.audience.partial_day_index,
                        total:
                          send.audience.partial_days_estimated ??
                          send.audience.partial_day_index,
                      })}
                    </p>
                  )}
                  {typeof send.audience.coverage_bps === "number" &&
                    (send.audience.coverage_total ?? 0) > 0 && (
                      <p className="mb-2 small">
                        {t("sending.detail.partialCoverage", {
                          pct: (send.audience.coverage_bps / 100).toFixed(1),
                          sent: send.audience.coverage_sent ?? 0,
                          total: send.audience.coverage_total ?? 0,
                        })}
                      </p>
                    )}
                  {(send.audience.partial_chain?.nodes?.length ?? 0) > 0 && (
                    <div className="mb-2">
                      <p className="small fw-medium mb-1">
                        {t("sending.detail.partialTimelineTitle")}
                      </p>
                      <ul className="list-unstyled small mb-0 ps-1">
                        {send.audience.partial_chain!.nodes.map((node) => (
                          <li key={node.id} className="mb-1">
                            <Link
                              to={`/sends/${node.id}`}
                              className={
                                node.id === send.id
                                  ? "fw-semibold"
                                  : "alert-link"
                              }
                            >
                              {t("sending.detail.partialTimelineNode", {
                                day: node.day_index || "—",
                                id: node.id,
                                status: t(`sending.status.${node.status}`, {
                                  defaultValue: node.status,
                                }),
                                sent: node.sent,
                              })}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {send.audience.suggest_raise_daily_cap ? (
                    <p className="mb-2 small border rounded px-2 py-2 bg-light">
                      {t("sending.detail.suggestRaiseDailyCap")}
                    </p>
                  ) : null}
                  {send.audience.excluded_tag_name &&
                    (send.audience.excluded_count ?? 0) > 0 && (
                      <p className="mb-2 small">
                        {t("sending.detail.partialExcludedTag", {
                          tag: send.audience.excluded_tag_name,
                          count: send.audience.excluded_count ?? 0,
                        })}{" "}
                        {!send.audience.excluded_tag_missing &&
                        send.audience.excluded_tag_id ? (
                          <>
                            <Link
                              to={`/contacts?tag_ids=${send.audience.excluded_tag_id}`}
                              className="alert-link"
                            >
                              {t("sending.detail.partialExcludedTagOpen")}
                            </Link>
                            {" · "}
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 align-baseline"
                              disabled={remainderBusy}
                              onClick={() => {
                                setRemainderBusy(true);
                                downloadExcludedContactsCsv(send.id)
                                  .then(() =>
                                    toast.success(
                                      t("sending.detail.partialExcludedCsvDone")
                                    )
                                  )
                                  .catch((e: { code?: string; message?: string }) => {
                                    if (e?.code === "PARTIAL_EXCLUDED_TAG_MISSING") {
                                      toast.error(
                                        t(
                                          "sending.detail.partialExcludedCsvTagMissing"
                                        )
                                      );
                                      return;
                                    }
                                    if (e?.code === "PARTIAL_EXCLUDED_EMPTY") {
                                      toast.info(
                                        t("sending.detail.partialExcludedCsvEmpty")
                                      );
                                      return;
                                    }
                                    toast.error(
                                      e?.message ||
                                        t("sending.detail.partialExcludedCsvError")
                                    );
                                  })
                                  .finally(() => setRemainderBusy(false));
                              }}
                            >
                              {t("sending.detail.partialExcludedCsv")}
                            </button>
                          </>
                        ) : null}
                      </p>
                    )}
                  {send.audience.excluded_tag_missing && (
                    <p className="mb-2 small text-danger">
                      {t("sending.detail.partialExcludedTagMissing")}
                    </p>
                  )}
                  {send.audience.remainder_over_daily_cap &&
                    !send.audience.continuation_send_id && (
                      <p className="mb-2 small">
                        {t("sending.detail.partialRemainderOverCap", {
                          excluded: send.audience.excluded_count ?? 0,
                          cap: send.audience.daily_cap_for_remainder ?? 0,
                        })}
                      </p>
                    )}
                  {send.audience.continuation_chain_capped ||
                  send.audience.partial_chain_capped ? (
                    <p className="mb-2 small text-warning">
                      {t("sending.detail.partialChainCapped", {
                        max:
                          send.audience.partial_max_continuation_days ?? 7,
                      })}
                    </p>
                  ) : null}
                  {send.audience.continuation_send_id ? (
                    <p className="mb-2">
                      {t("sending.detail.partialContinuationQueued", {
                        id: send.audience.continuation_send_id,
                      })}{" "}
                      <Link
                        to={`/sends/${send.audience.continuation_send_id}`}
                        className="alert-link"
                      >
                        {t("sending.detail.partialContinuationLink")}
                      </Link>
                      {send.audience.continuation_will_partial ? (
                        <span className="d-block small text-muted mt-1">
                          {t("sending.detail.partialContinuationWillPartial")}
                        </span>
                      ) : null}
                    </p>
                  ) : (send.audience.excluded_tag_id ?? 0) > 0 &&
                    (send.audience.excluded_count ?? 0) > 0 &&
                    !send.audience.excluded_tag_missing &&
                    !["queued", "pending_approval", "preparing"].includes(
                      send.status
                    ) ? (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <Button
                        color="info"
                        size="sm"
                        outline
                        disabled={remainderBusy}
                        onClick={() => {
                          setRemainderBusy(true);
                          queuePartialRemainder(send.id)
                            .then((res) => {
                              toast.success(
                                res.message ||
                                  t("sending.detail.queueRemainderDone")
                              );
                              void load(true);
                            })
                            .catch((e: { message?: string }) => {
                              toast.error(
                                e?.message || t("sending.error.generic")
                              );
                            })
                            .finally(() => setRemainderBusy(false));
                        }}
                      >
                        {remainderBusy ? (
                          <Spinner size="sm" className="me-1" />
                        ) : null}
                        {t("sending.detail.queueRemainder")}
                      </Button>
                      <span className="small text-muted">
                        {t("sending.detail.queueRemainderHelp")}
                      </span>
                    </div>
                  ) : null}
                  {send.status !== "cancelled" &&
                    send.status !== "completed" &&
                    send.status !== "failed" && (
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        {send.audience.partial_chain?.chain_paused ? (
                          <Button
                            color="success"
                            size="sm"
                            outline
                            disabled={remainderBusy}
                            onClick={() => {
                              setRemainderBusy(true);
                              resumePartialChain(send.id)
                                .then((res) => {
                                  toast.success(
                                    res.message ||
                                      t("sending.detail.resumeChainDone")
                                  );
                                  void load(true);
                                })
                                .catch((e: { message?: string }) => {
                                  toast.error(
                                    e?.message || t("sending.error.generic")
                                  );
                                })
                                .finally(() => setRemainderBusy(false));
                            }}
                          >
                            {t("sending.detail.resumeChain")}
                          </Button>
                        ) : (
                          <Button
                            color="warning"
                            size="sm"
                            outline
                            disabled={remainderBusy}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  t("sending.detail.pauseChainConfirm")
                                )
                              ) {
                                return;
                              }
                              setRemainderBusy(true);
                              pausePartialChain(send.id)
                                .then((res) => {
                                  toast.success(
                                    res.message ||
                                      t("sending.detail.pauseChainDone")
                                  );
                                  void load(true);
                                })
                                .catch((e: { message?: string }) => {
                                  toast.error(
                                    e?.message || t("sending.error.generic")
                                  );
                                })
                                .finally(() => setRemainderBusy(false));
                            }}
                          >
                            {t("sending.detail.pauseChain")}
                          </Button>
                        )}
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          disabled={remainderBusy}
                          onClick={() => setCancelChainOpen(true)}
                        >
                          {t("sending.detail.cancelChain")}
                        </Button>
                      </div>
                    )}
                </Alert>
              )}
              {send.audience?.continuation_of_send_id ? (
                <Alert color="secondary" className="mb-3 py-2">
                  {t("sending.detail.continuationOf", {
                    id: send.audience.continuation_of_send_id,
                  })}{" "}
                  <Link
                    to={`/sends/${send.audience.continuation_of_send_id}`}
                    className="alert-link"
                  >
                    #{send.audience.continuation_of_send_id}
                  </Link>
                  {(send.audience.partial_day_index ?? 0) > 0 ? (
                    <span className="d-block small mt-1">
                      {t("sending.detail.partialProgress", {
                        day: send.audience.partial_day_index,
                        total:
                          send.audience.partial_days_estimated ??
                          send.audience.partial_day_index,
                      })}
                    </span>
                  ) : null}
                </Alert>
              ) : null}
              {(send.send_interval_seconds ?? 0) > 0 && (
                <p className="text-muted small mb-2">
                  {t("sending.detail.paceLabel", {
                    seconds: send.send_interval_seconds,
                  })}
                </p>
              )}
              {send.deadline_at && (
                <p className="text-muted small mb-2">
                  {t("sending.detail.deadlineLabel", {
                    when: new Date(send.deadline_at).toLocaleString(
                      i18n.language || "pt-BR"
                    ),
                  })}
                </p>
              )}
              {isBounceRateHigh(send.bounce_rate, bounceWarnThreshold) && (
                <Alert
                  color="warning"
                  className="mb-3 py-2 d-flex flex-wrap justify-content-between align-items-center gap-2"
                >
                  <span>
                    {t("sending.detail.bounceWarn", {
                      rate: formatRatePercent(send.bounce_rate, locale),
                    })}
                  </span>
                  <Button
                    color="warning"
                    size="sm"
                    outline
                    onClick={() => onRecipientStatusChange("bounced")}
                  >
                    {t("sending.detail.bounceWarnCta")}
                  </Button>
                </Alert>
              )}
              <Card className="mb-3">
                <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div>
                    <h5 className="card-title mb-1">
                      {send.campaign?.name ||
                        t("sending.list.campaignFallback", {
                          id: send.campaign_id,
                        })}
                      {send.is_test && (
                        <Badge color="secondary" className="ms-2" pill>
                          {t("sending.badge.test")}
                        </Badge>
                      )}
                    </h5>
                    <p className="text-muted mb-0">{send.subject}</p>
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button
                      color="soft-secondary"
                      size="sm"
                      type="button"
                      onClick={() => void copySendSnapshot()}
                      title={t("sending.detail.copySnapshot.title")}
                    >
                      <i className="ri-file-copy-line me-1 align-middle" aria-hidden />
                      {t("sending.detail.copySnapshot.button")}
                    </Button>
                    <Badge
                      color={statusColor(send.status)}
                      className="text-uppercase fs-6"
                    >
                      {t(`sending.status.${send.status}`)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  {send.queued > 0 ? (
                    <div className="mb-3">
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                        <span className="fw-medium">
                          {t("sending.detail.progress.label", {
                            sent: send.sent.toLocaleString(locale),
                            queued: send.queued.toLocaleString(locale),
                          })}
                        </span>
                        <span className="text-muted small">
                          {t("sending.detail.progress.percent", {
                            percent: Math.min(
                              100,
                              Math.round((send.sent / send.queued) * 100)
                            ),
                          })}
                        </span>
                      </div>
                      <Progress
                        value={Math.min(
                          100,
                          Math.round((send.sent / send.queued) * 100)
                        )}
                        className="progress-sm"
                        animated={isSendInProgress(send.status)}
                        color={
                          send.status === "failed"
                            ? "danger"
                            : send.status === "completed"
                              ? "success"
                              : "primary"
                        }
                      />
                      {(send.pending_count ?? 0) > 0 ? (
                        <p className="text-muted small mb-0 mt-1">
                          {t("sending.detail.progress.pendingHint", {
                            pending: (send.pending_count ?? 0).toLocaleString(
                              locale
                            ),
                          })}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <Row className="g-3 text-muted small">
                    <Col md={3}>
                      <strong className="d-block text-body">
                        {t("sending.detail.from")}
                      </strong>
                      {send.from_name} &lt;{send.from_email}&gt;
                    </Col>
                    <Col md={3}>
                      <strong className="d-block text-body">
                        {t("sending.detail.scheduledAt")}
                      </strong>
                      {send.scheduled_at
                        ? formatTenantDate(send.scheduled_at)
                        : "—"}
                    </Col>
                    <Col md={3}>
                      <strong className="d-block text-body">
                        {t("sending.detail.startedAt")}
                      </strong>
                      {send.started_at
                        ? formatTenantDate(send.started_at)
                        : "—"}
                    </Col>
                    <Col md={3}>
                      <strong className="d-block text-body">
                        {t("sending.detail.finishedAt")}
                      </strong>
                      {send.finished_at
                        ? formatTenantDate(send.finished_at)
                        : "—"}
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <h6 className="text-muted text-uppercase mb-2">
                {t("sending.detail.ratesTitle")}
              </h6>
              <Row className="g-3 mb-3">
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.openRate")}
                    value={formatRatePercent(send.open_rate, locale)}
                    icon="ri-mail-open-line"
                    hint={t("sending.detail.rates.openRateHint")}
                    onClick={() => filterByEngagement("opened")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.clickRate")}
                    value={formatRatePercent(send.click_rate, locale)}
                    icon="ri-cursor-line"
                    hint={t("sending.detail.rates.clickRateHint")}
                    onClick={() => filterByEngagement("clicked")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.ctor")}
                    value={formatRatePercent(send.ctor, locale)}
                    icon="ri-focus-3-line"
                    hint={t("sending.detail.rates.ctorHint")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.deliveryRate")}
                    value={formatRatePercent(send.delivery_rate, locale)}
                    icon="ri-checkbox-circle-line"
                    hint={t("sending.detail.rates.deliveryRateHint")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.bounceRate")}
                    value={formatRatePercent(send.bounce_rate, locale)}
                    icon="ri-error-warning-line"
                    hint={t("sending.detail.rates.bounceRateHint")}
                    onClick={() => filterByStatus("bounced")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.complaintRate")}
                    value={formatRatePercent(send.complaint_rate, locale)}
                    icon="ri-alarm-warning-line"
                    hint={t("sending.detail.rates.complaintRateHint")}
                    onClick={() => filterByStatus("complained")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.rates.unsubscribeRate")}
                    value={formatRatePercent(send.unsubscribe_rate, locale)}
                    icon="ri-user-unfollow-line"
                    hint={t("sending.detail.rates.unsubscribeRateHint")}
                  />
                </Col>
              </Row>

              <h6 className="text-muted text-uppercase mb-2">
                {t("sending.detail.countsTitle")}
              </h6>
              {send.sent > 0 && send.opened === 0 && send.clicked === 0 ? (
                <Alert color="info" className="mb-3">
                  {t("sending.detail.engagementEmptyHint")}
                </Alert>
              ) : null}
              <Row className="g-3 mb-3">
                <Col xl={3} md={6}>
                  {/* queued = tamanho na prepare; o restante é pending_count (DOC 14 § 10.5.5). */}
                  <KpiCard
                    label={t("sending.detail.kpi.queued")}
                    value={send.queued}
                    icon="ri-stack-line"
                    hint={t("sending.detail.kpi.queuedHint")}
                    onClick={focusRecipientsList}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.sent")}
                    value={send.sent}
                    icon="ri-mail-send-line"
                    hint={t("sending.detail.kpi.sentHint")}
                    onClick={() => filterByStatus("sent")}
                  />
                </Col>
                {(send.pending_count ?? 0) > 0 ? (
                  <Col xl={3} md={6}>
                    <KpiCard
                      label={t("sending.detail.kpi.pending")}
                      value={send.pending_count ?? 0}
                      icon="ri-time-line"
                      hint={t("sending.detail.kpi.pendingHint")}
                    />
                  </Col>
                ) : null}
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.opened")}
                    value={send.opened}
                    icon="ri-mail-open-line"
                    hint={t("sending.detail.kpi.openedHint")}
                    onClick={() => filterByEngagement("opened")}
                  />
                  {(send.provisional_opened ?? 0) > 0 ? (
                    <p
                      className="text-muted small mb-0 mt-1 px-1"
                      title={t("sending.detail.kpi.openedProvisionalHint")}
                    >
                      {t("sending.detail.kpi.openedProvisional", {
                        count: (send.provisional_opened ?? 0).toLocaleString(
                          locale
                        ),
                      })}
                    </p>
                  ) : null}
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.clicked")}
                    value={send.clicked}
                    icon="ri-cursor-line"
                    hint={t("sending.detail.kpi.clickedHint")}
                    onClick={() => filterByEngagement("clicked")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.bounced")}
                    value={send.bounced}
                    icon="ri-error-warning-line"
                    hint={t("sending.detail.kpi.bouncedHint")}
                    onClick={() => filterByStatus("bounced")}
                  />
                  {(send.hard_bounced != null || send.soft_bounced != null) &&
                  (send.hard_bounced ?? 0) + (send.soft_bounced ?? 0) > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mt-1 px-1">
                      <Badge
                        color="danger"
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        title={t("sending.detail.kpi.hardBouncedHint")}
                        onClick={() => filterByBounceKind("hard")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            filterByBounceKind("hard");
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {t("sending.detail.kpi.hardBounced")}:{" "}
                        {(send.hard_bounced ?? 0).toLocaleString(locale)}
                      </Badge>
                      <Badge
                        color="warning"
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        title={t("sending.detail.kpi.softBouncedHint")}
                        onClick={() => filterByBounceKind("soft")}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            filterByBounceKind("soft");
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {t("sending.detail.kpi.softBounced")}:{" "}
                        {(send.soft_bounced ?? 0).toLocaleString(locale)}
                      </Badge>
                    </div>
                  ) : null}
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.failed")}
                    value={send.failed}
                    icon="ri-close-circle-line"
                    hint={t("sending.detail.kpi.failedHint")}
                    onClick={() => filterByStatus("failed")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.unsubscribed")}
                    value={send.unsubscribed}
                    icon="ri-user-unfollow-line"
                    hint={t("sending.detail.kpi.unsubscribedHint")}
                  />
                </Col>
                <Col xl={3} md={6}>
                  <KpiCard
                    label={t("sending.detail.kpi.complained")}
                    value={send.complained}
                    icon="ri-alarm-warning-line"
                    hint={t("sending.detail.kpi.complainedHint")}
                    onClick={() => filterByStatus("complained")}
                  />
                </Col>
                {send.goal_count != null ? (
                  <Col xl={3} md={6}>
                    <KpiCard
                      label={t("sending.detail.kpi.goal")}
                      value={send.goal_count}
                      icon="ri-flag-line"
                      hint={t("sending.detail.kpi.goalHint")}
                    />
                  </Col>
                ) : null}
              </Row>

              {Array.isArray(send.links) && send.links.length > 0 ? (
                <Card className="mb-3">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("sending.detail.linksTitle")}
                    </h5>
                    <p className="text-muted mb-0 mt-1 small">
                      {t("sending.detail.linksHint")}
                    </p>
                    <p className="text-muted mb-0 mt-1 small">
                      {t("sending.detail.linksGoalVsClicks")}
                    </p>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">{t("sending.detail.links.url")}</th>
                            <th scope="col" className="text-center">
                              {t("sending.detail.links.goal")}
                            </th>
                            <th scope="col" className="text-end">
                              {t("sending.detail.links.clicks")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {send.links.map((link) => (
                            <tr key={link.id}>
                              <td>
                                <span
                                  className="text-break"
                                  title={link.url}
                                >
                                  {link.url.length > 80
                                    ? `${link.url.slice(0, 77)}…`
                                    : link.url}
                                </span>
                                {link.is_goal ? (
                                  <Badge color="success" className="ms-2">
                                    {t("sending.detail.links.goal")}
                                  </Badge>
                                ) : null}
                              </td>
                              <td className="text-center">
                                <Input
                                  type="checkbox"
                                  checked={Boolean(link.is_goal)}
                                  disabled={linkGoalBusy === link.id}
                                  aria-label={t(
                                    "sending.detail.links.goalToggle"
                                  )}
                                  onChange={() =>
                                    void toggleLinkGoal(
                                      link.id,
                                      Boolean(link.is_goal)
                                    )
                                  }
                                />
                              </td>
                              <td className="text-end fw-semibold">
                                {link.click_count}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              ) : null}

              <Card className="mb-3" id="send-recipients">
                <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <h5 className="card-title mb-0">
                    {t("sending.recipients.title")}
                  </h5>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Label
                      className="mb-0 small text-muted"
                      for="recipient-status"
                    >
                      {t("sending.recipients.filterLabel")}
                    </Label>
                    <Input
                      id="recipient-status"
                      type="select"
                      bsSize="sm"
                      style={{ width: "auto", minWidth: 140 }}
                      value={recipientStatus}
                      onChange={(e) => onRecipientStatusChange(e.target.value)}
                      aria-label={t("sending.recipients.filterLabel")}
                    >
                      <option value="">
                        {t("sending.recipients.filter.all")}
                      </option>
                      <option value="bounced">
                        {t("sending.recipients.status.bounced")}
                      </option>
                      <option value="complained">
                        {t("sending.recipients.status.complained")}
                      </option>
                      <option value="failed">
                        {t("sending.recipients.status.failed")}
                      </option>
                      <option value="sent">
                        {t("sending.recipients.status.sent")}
                      </option>
                      <option value="pending">
                        {t("sending.recipients.status.pending")}
                      </option>
                      <option value="skipped">
                        {t("sending.recipients.status.skipped")}
                      </option>
                    </Input>
                    <Label
                      className="mb-0 small text-muted"
                      for="recipient-engagement"
                    >
                      {t("sending.recipients.engagementLabel")}
                    </Label>
                    <Input
                      id="recipient-engagement"
                      type="select"
                      bsSize="sm"
                      style={{ width: "auto", minWidth: 120 }}
                      value={recipientEngagement}
                      onChange={(e) =>
                        onRecipientEngagementChange(e.target.value)
                      }
                      aria-label={t("sending.recipients.engagementLabel")}
                    >
                      <option value="">
                        {t("sending.recipients.engagement.all")}
                      </option>
                      {ENGAGEMENT_FILTERS.map((eng) => (
                        <option key={eng} value={eng}>
                          {t(`sending.recipients.engagement.${eng}`)}
                        </option>
                      ))}
                    </Input>
                    <Label
                      className="mb-0 small text-muted"
                      for="recipient-bounce-kind"
                    >
                      {t("sending.recipients.bounceKindLabel")}
                    </Label>
                    <Input
                      id="recipient-bounce-kind"
                      type="select"
                      bsSize="sm"
                      style={{ width: "auto", minWidth: 130 }}
                      value={recipientBounceKind}
                      onChange={(e) =>
                        onRecipientBounceKindChange(e.target.value)
                      }
                      aria-label={t("sending.recipients.bounceKindLabel")}
                    >
                      <option value="">
                        {t("sending.recipients.bounceKind.all")}
                      </option>
                      {BOUNCE_KIND_FILTERS.map((kind) => (
                        <option key={kind} value={kind}>
                          {t(`sending.recipients.bounceKind.${kind}`)}
                        </option>
                      ))}
                    </Input>
                    <Button
                      color="soft-primary"
                      size="sm"
                      disabled={recipientsLoading || tagBusy}
                      onClick={() => openTagModal()}
                    >
                      {t("sending.recipients.tagButton")}
                    </Button>
                    <Button
                      color="soft-secondary"
                      size="sm"
                      disabled={exportBusy || recipientsLoading}
                      onClick={() => exportFilteredCsv()}
                    >
                      {exportBusy ? (
                        <Spinner size="sm" />
                      ) : (
                        t("sending.recipients.exportCsv")
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {recipientStatus === "bounced" || recipientBounceKind ? (
                    <Alert color="info" className="mb-3">
                      <div>{t("sending.recipients.bounceHint")}</div>
                      <ul className="mb-0 mt-2 small">
                        <li>
                          <strong>{t("sending.recipients.bounceKind.hard")}:</strong>{" "}
                          {t("sending.recipients.bounceKind.hardHint")}
                        </li>
                        <li>
                          <strong>{t("sending.recipients.bounceKind.soft")}:</strong>{" "}
                          {t("sending.recipients.bounceKind.softHint")}
                        </li>
                      </ul>
                      <p className="mb-0 mt-2 small text-muted">
                        {t("sending.recipients.bounceGlossary")}
                      </p>
                    </Alert>
                  ) : null}
                  {recipientsLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" size="sm" />
                    </div>
                  ) : recipientsError ? (
                    <Alert color="danger" className="mb-0">
                      {recipientsError}{" "}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 align-baseline"
                        onClick={() => loadRecipients()}
                      >
                        {t("sending.recipients.retry")}
                      </button>
                    </Alert>
                  ) : recipients.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted mb-0">
                        {recipientStatus ||
                        recipientEngagement ||
                        recipientBounceKind
                          ? t("sending.recipients.emptyFiltered")
                          : t("sending.recipients.empty")}
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted small mb-2">
                        {t("sending.recipients.totalCount", {
                          count: recipientTotal,
                        })}
                      </p>
                      <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("sending.recipients.table.email")}</th>
                              <th>{t("sending.recipients.table.status")}</th>
                              <th>{t("sending.recipients.table.opened")}</th>
                              <th>{t("sending.recipients.table.clicked")}</th>
                              <th style={{ width: 120 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {recipients.map((r) => (
                              <tr key={r.id}>
                                <td>{r.email}</td>
                                <td>
                                  <Badge color={recipientStatusColor(r.status)}>
                                    {t(`sending.recipients.status.${r.status}`, {
                                      defaultValue: r.status,
                                    })}
                                  </Badge>
                                  {r.bounce_kind === "hard" ||
                                  r.bounce_kind === "soft" ? (
                                    <Badge
                                      color={
                                        r.bounce_kind === "hard"
                                          ? "danger"
                                          : "warning"
                                      }
                                      className="ms-1"
                                      title={t(
                                        `sending.recipients.bounceKind.${r.bounce_kind}Hint`
                                      )}
                                    >
                                      {t(
                                        `sending.recipients.bounceKind.${r.bounce_kind}`
                                      )}
                                    </Badge>
                                  ) : null}
                                </td>
                                <td>
                                  {r.opened ? (
                                    <span
                                      title={
                                        r.opened_at
                                          ? formatTenantDate(r.opened_at)
                                          : undefined
                                      }
                                    >
                                      {t("sending.recipients.yes")}
                                    </span>
                                  ) : (
                                    t("sending.recipients.no")
                                  )}
                                </td>
                                <td>
                                  {r.clicked ? (
                                    <span
                                      title={
                                        r.clicked_at
                                          ? formatTenantDate(r.clicked_at)
                                          : undefined
                                      }
                                    >
                                      {t("sending.recipients.yes")}
                                    </span>
                                  ) : (
                                    t("sending.recipients.no")
                                  )}
                                </td>
                                <td className="text-end">
                                  {r.contact_id ? (
                                    <Link
                                      to={`/contacts?search=${encodeURIComponent(r.email)}`}
                                      className="btn btn-sm btn-soft-primary"
                                    >
                                      {t("sending.recipients.openContact")}
                                    </Link>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {recipientLastPage > 1 && (
                        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                          <span className="text-muted small">
                            {t("sending.recipients.pagination", {
                              page: recipientPage,
                              lastPage: recipientLastPage,
                              total: recipientTotal,
                            })}
                          </span>
                          <div className="d-flex gap-2">
                            <Button
                              color="light"
                              size="sm"
                              disabled={recipientPage <= 1 || recipientsLoading}
                              onClick={() =>
                                setRecipientPage((p) => Math.max(1, p - 1))
                              }
                            >
                              {t("sending.recipients.prev")}
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              disabled={
                                recipientPage >= recipientLastPage ||
                                recipientsLoading
                              }
                              onClick={() =>
                                setRecipientPage((p) =>
                                  Math.min(recipientLastPage, p + 1)
                                )
                              }
                            >
                              {t("sending.recipients.next")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("sending.detail.previewTitle")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {send.html_snapshot ? (
                    // iframe isola CSS/HTML do e-mail — evita vazamento no layout do painel.
                    <div
                      className="border rounded bg-white"
                      style={{ maxHeight: 480, overflow: "auto" }}
                    >
                      <iframe
                        title={t("sending.detail.previewTitle")}
                        sandbox=""
                        srcDoc={send.html_snapshot}
                        style={{
                          width: "100%",
                          minHeight: 360,
                          border: 0,
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-muted mb-0">
                      {t("sending.detail.previewEmpty")}
                    </p>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>

      <Modal
        isOpen={tagModalOpen}
        toggle={() => !tagBusy && setTagModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !tagBusy && setTagModalOpen(false)}>
          {t("sending.recipients.tagTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">{t("sending.recipients.tagHint")}</p>
          {tagLoading ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
              <p className="text-muted small mb-0 mt-2">
                {t("sending.recipients.tagLoading")}
              </p>
            </div>
          ) : tagOptions.length === 0 ? (
            <Alert color="warning" className="mb-0">
              {t("sending.recipients.tagEmpty")}
            </Alert>
          ) : (
            <>
              <Label for="tag-select">{t("sending.recipients.tagLabel")}</Label>
              <Input
                id="tag-select"
                type="select"
                value={tagId === "" ? "" : String(tagId)}
                onChange={(e) =>
                  setTagId(e.target.value ? Number(e.target.value) : "")
                }
              >
                <option value="">
                  {t("sending.recipients.tagPlaceholder")}
                </option>
                {tagOptions.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </Input>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={tagBusy}
            onClick={() => setTagModalOpen(false)}
          >
            {t("sending.recipients.tagCancel")}
          </Button>
          <Button
            color="primary"
            disabled={tagBusy || tagLoading || !tagId || tagOptions.length === 0}
            onClick={confirmTag}
          >
            {tagBusy ? <Spinner size="sm" /> : t("sending.recipients.tagSubmit")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={cancelOpen}
        toggle={() => !cancelBusy && setCancelOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !cancelBusy && setCancelOpen(false)}>
          {t("sending.detail.cancelTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("sending.detail.cancelConfirm")}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={cancelBusy}
            onClick={() => setCancelOpen(false)}
          >
            {t("sending.modals.cancel")}
          </Button>
          <Button color="danger" disabled={cancelBusy} onClick={confirmCancel}>
            {cancelBusy ? (
              <Spinner size="sm" />
            ) : (
              t("sending.detail.cancelConfirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={cancelChainOpen}
        toggle={() => !remainderBusy && setCancelChainOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !remainderBusy && setCancelChainOpen(false)}>
          {t("sending.detail.cancelChainTitle")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {t("sending.detail.cancelChainConfirmCounts", {
              cancellable:
                send?.audience?.partial_chain?.cancellable_count ?? 0,
              sent: send?.audience?.partial_chain?.already_sent_total ?? 0,
              terminal: send?.audience?.partial_chain?.terminal_count ?? 0,
            })}
          </p>
          <p className="mb-0 small text-muted">
            {t("sending.detail.cancelChainConfirm")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={remainderBusy}
            onClick={() => setCancelChainOpen(false)}
          >
            {t("sending.modals.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={remainderBusy || !send}
            onClick={() => {
              if (!send) return;
              setRemainderBusy(true);
              cancelPartialChain(send.id)
                .then((res) => {
                  toast.success(
                    res.message || t("sending.detail.cancelChainDone")
                  );
                  setCancelChainOpen(false);
                  void load(true);
                })
                .catch((e: { message?: string }) => {
                  toast.error(e?.message || t("sending.error.generic"));
                })
                .finally(() => setRemainderBusy(false));
            }}
          >
            {remainderBusy ? (
              <Spinner size="sm" />
            ) : (
              t("sending.detail.cancelChainConfirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <SendApprovalRejectModal
        isOpen={rejectOpen}
        busy={approvalBusy}
        reason={rejectionReason}
        onReasonChange={setRejectionReason}
        onClose={() => setRejectOpen(false)}
        onConfirm={() => void reject()}
      />

      <ToastContainer closeButton={false} limit={3} />
    </React.Fragment>
  );
};

export default SendDetail;
